/**
 * WebMCP tool registration (https://webmachinelearning.github.io/webmcp/).
 *
 * Exposes site actions to in-browser AI agents. The API surface is still
 * settling: the spec anchors the ModelContext on `document.modelContext`,
 * while Chrome's early preview ships `navigator.modelContext` — and older
 * builds only expose `provideContext()` instead of `registerTool()`. We
 * feature-detect all of these and no-op when none exist.
 */

import { createNewLeadSinglePane } from './api'

interface WebMcpToolDescriptor {
  name: string
  description: string
  inputSchema: Record<string, unknown>
  execute: (input: Record<string, unknown>) => Promise<unknown>
}

interface ModelContext {
  registerTool?: (tool: WebMcpToolDescriptor) => Promise<void>
  provideContext?: (context: { tools: WebMcpToolDescriptor[] }) => void
}

interface ContactSalesInput {
  email?: string
  fullName?: string
  companyWebsite?: string
  jobTitle?: string
  message?: string
  monthlyApiCalls?: string
}

// Must match the values the contact-sales form submits (see
// components/newIndustryComponents/CtaSection) — HubSpot rejects other values.
const API_CALL_VOLUMES = ['Less than 1 million', 'Between 1 million and 9 million', 'More than 9 million']

const contactSalesTool: WebMcpToolDescriptor = {
  name: 'contact_sales',
  description:
    'Submit a contact request to the Fingerprint sales team. Use this when the user wants to talk to sales, ' +
    'get a quote, request a demo, or discuss using Fingerprint device intelligence. Requires a business email ' +
    'address (personal/free email providers are rejected). The sales team replies by email.',
  inputSchema: {
    type: 'object',
    required: ['email', 'fullName', 'companyWebsite', 'message'],
    properties: {
      email: {
        type: 'string',
        description: 'Business email address of the person contacting sales. Free email providers are rejected.',
      },
      fullName: {
        type: 'string',
        description: 'Full name (first and last) of the person contacting sales.',
      },
      companyWebsite: {
        type: 'string',
        description: 'Company website URL.',
      },
      jobTitle: {
        type: 'string',
        description: 'Job title of the person contacting sales.',
      },
      message: {
        type: 'string',
        description: 'What the user wants to discuss with the sales team, including their use case.',
      },
      monthlyApiCalls: {
        type: 'string',
        enum: API_CALL_VOLUMES,
        description: 'Estimated monthly identification API call volume.',
      },
    },
  },
  execute: async (input: ContactSalesInput) => {
    const { email, fullName, companyWebsite, jobTitle, message, monthlyApiCalls } = input

    if (!email || !fullName || !companyWebsite || !message) {
      return textResult('Missing required fields: email, fullName, companyWebsite and message are all required.')
    }
    if (monthlyApiCalls && !API_CALL_VOLUMES.includes(monthlyApiCalls)) {
      return textResult(`Invalid monthlyApiCalls value. Allowed values: ${API_CALL_VOLUMES.join(' | ')}.`)
    }

    try {
      const response = await createNewLeadSinglePane({
        email,
        formName: fullName,
        url: companyWebsite,
        jobTitle,
        description: message,
        apiCallsQuestion: monthlyApiCalls,
        landingPage: window.location.href,
        previousPage: document.referrer || undefined,
      })
      // The API signals validation failures with HTTP 200 + { ok: false },
      // so the body — not the status — is the source of truth.
      const body = await response.json()
      if (body?.ok) {
        return textResult(
          'Contact request submitted successfully. The Fingerprint sales team will reply to the provided email address.'
        )
      }
      return textResult(
        `Contact request was not accepted: ${body?.error?.message ?? 'validation failed'}. ` +
          'Check that the email is a valid business address (free email providers are rejected) and try again.'
      )
    } catch {
      return textResult('Contact request failed due to a network error. Please try again.')
    }
  },
}

function textResult(text: string) {
  return { content: [{ type: 'text', text }] }
}

export function registerWebMcpTools(): void {
  try {
    const modelContext: ModelContext | undefined =
      (typeof navigator !== 'undefined' ? (navigator as { modelContext?: ModelContext }).modelContext : undefined) ??
      (typeof document !== 'undefined' ? (document as { modelContext?: ModelContext }).modelContext : undefined)

    if (!modelContext) {
      return
    }

    if (typeof modelContext.registerTool === 'function') {
      void modelContext.registerTool(contactSalesTool)
    } else if (typeof modelContext.provideContext === 'function') {
      // provideContext replaces the whole tool set — fine while this is the
      // only registration point; revisit if tools get registered elsewhere.
      modelContext.provideContext({ tools: [contactSalesTool] })
    }
  } catch {
    // Agent-only enhancement — never let it break the page.
  }
}
